import { useCallback, useEffect, memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { TimelineWithSections } from '@guidde-co/shared.video-player.timeline-with-sections'
import { CustomControls } from '@guidde-co/shared.video-player.controls.custom-controls'

import { Box } from '@material-ui/core'

import { VideoOverlayWrapper } from 'UI/Routes/share/common/VideoOverlayWrapper'
import { ControlledVideoPlayer, SpacedGroup, TrimTimePickers } from 'UI/Components'

import { CtaControl, ToggleSidePanel } from './hoverControls'
import { EditableTimelineWithSections } from './EditableTimelineWithSections'
import { VideoEditorButtons } from './VideoEditorButtons'
import { CallToAction } from './CallToAction'

import { useVideoSections } from './use-video-sections'

import {
    orgFeatures,
    useAnonymAuth,
    useBoolean,
    UseBooleanType,
    useOrgFeatureFlags,
    useQuery,
    useQueryParams
} from 'hooks'
import { closeVideoEditor, setCurrentTime } from 'ducks/actions'
import { roundToHundredth, logToAnalytics, playbookToAnalyticsProps, isQG } from 'modules'
import { PlaybookType, VideoRefType, CanvasRefType } from 'app/types'

type Props = {
    playbook: PlaybookType
    src: string
    videoRef: VideoRefType
    canvasRef?: CanvasRefType
    isEditMode?: boolean
    showSidePanel?: UseBooleanType
    showVideoOverlay?: UseBooleanType
    hideCurrentChapters?: boolean
    onEnded?: () => void
}

export const VideoEditor = memo(
    ({
        playbook,
        src,
        videoRef,
        canvasRef,
        isEditMode,
        showSidePanel,
        showVideoOverlay,
        hideCurrentChapters = false,
        onEnded
    }: Props) => {
        const dispatch = useDispatch()

        const { isAnonymous } = useAnonymAuth()

        const query = useQueryParams()

        const { showCaptions } = useOrgFeatureFlags(isAnonymous ? '' : playbook.uploadedByOrgId)
        // it's a possible "fix" of Firebase crash on public pages
        const $publicFF = useQuery<Record<orgFeatures, boolean>>(
            isAnonymous ? `/c/v1/feature-flags-public?orgId=${playbook.uploadedByOrgId}` : '',
            {
                method: 'GET'
            }
        )

        const captions = useBoolean(
            Boolean(isAnonymous ? $publicFF.data?.showCaptions : showCaptions)
        )

        useVideoSections({ playbook, isEditMode })

        const videoFocussed = useBoolean()

        const { duration, sections, currentTime } = useSelector(state => state.videoEditor)

        const handleTimeUpdated = (e: any) => {
            if (e?.target?.currentTime === undefined || !e?.target?.duration) return null

            if (isEditMode) dispatch(setCurrentTime(e.target.currentTime))

            if (
                e?.target?.currentTime >=
                (duration / 100) * sections?.[sections.length - 1]?.rangeValues[1]
            ) {
                e.target.pause()
            }
        }

        const updateTimeByPercentage = useCallback(
            (percentage: number) => {
                if (videoRef?.current) {
                    const newTime = (duration / 100) * percentage || 0

                    return (videoRef.current.currentTime = newTime)
                }
            },
            [duration, videoRef]
        )

        const updateTimeBySeconds = useCallback(
            (seconds: number) => {
                if (videoRef?.current) {
                    return (videoRef.current.currentTime = seconds)
                }
            },
            [videoRef]
        )

        const handlePlay = () => {
            const endTime = (duration / 100) * sections?.[sections.length - 1]?.rangeValues[1]

            if (roundToHundredth(currentTime) >= roundToHundredth(endTime)) {
                if (!videoRef.current) return

                videoRef.current.currentTime = (duration / 100) * sections[0].rangeValues[0]
            }
            return videoRef?.current?.play()
        }

        // using for force video reload
        useEffect(() => {
            if (src) {
                return videoRef.current?.load()
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [src])

        // @ts-ignore Close video editor mode on unmount
        useEffect(() => {
            return () => dispatch(closeVideoEditor())
        }, [dispatch])

        return (
            <>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                    position="relative"
                    onMouseEnter={videoFocussed.setTrue}
                    onMouseLeave={videoFocussed.setFalse}
                >
                    <CallToAction
                        duration={playbook.duration}
                        videoElement={videoRef.current}
                        cta={playbook.cta}
                        onClick={() => {
                            logToAnalytics('openCta', {
                                ...playbookToAnalyticsProps(playbook),
                                ...{
                                    cta: playbook.cta,
                                    orgName: query.get('track_link_name') || ''
                                }
                            })
                        }}
                    />

                    <Box style={{ width: '100%' }}>
                        <ControlledVideoPlayer
                            key={src}
                            ref={videoRef}
                            playbookUrl={src}
                            subtitlesUrl={playbook.subtitlesUrl}
                            showCaptions={captions.isFalse}
                            roundBorders={isQG(playbook)}
                            link={playbook.link}
                            isPlaylist={playbook.isPlaylist}
                            playbookId={playbook.id}
                            controls={false}
                            onTimeUpdate={handleTimeUpdated}
                            onPlay={() => {
                                showVideoOverlay?.setFalse()
                                handlePlay()
                                logToAnalytics(
                                    isEditMode ? 'editorPlay' : 'videoPlayClicked',
                                    playbookToAnalyticsProps(playbook)
                                )
                            }}
                            onPause={() => {
                                logToAnalytics(
                                    isEditMode ? 'editorStop' : 'videoStopClicked',
                                    playbookToAnalyticsProps(playbook)
                                )
                            }}
                            onEnded={() => {
                                showVideoOverlay?.setTrue()
                                onEnded?.()
                            }}
                            hoverControls={
                                <>
                                    {duration && !isQG(playbook) && (
                                        <>
                                            <ToggleSidePanel showSidePanel={showSidePanel} />
                                            {isEditMode && <CtaControl playbook={playbook} />}
                                        </>
                                    )}
                                </>
                            }
                        />
                    </Box>

                    {showVideoOverlay?.isTrue && (
                        <Box position="absolute" zIndex={2} top={0} bottom={0} left={0} right={0}>
                            <VideoOverlayWrapper
                                showVideoOverlay={showVideoOverlay}
                                playbook={playbook}
                                videoRef={videoRef}
                            />
                        </Box>
                    )}
                </Box>

                <CustomControls
                    isFocused={videoFocussed.isTrue}
                    videoElement={videoRef.current}
                    onCaptionsClick={captions.setTrue}
                    captionsInitialState={captions.isTrue}
                    hideCurrentChapters={hideCurrentChapters}
                    chapters={
                        isEditMode
                            ? [...sections.slice(1, sections.length - 1)] // remove hidden first and last elements (used for video editor only)
                            : playbook.slicingSuggestion || []
                    }
                />
                <Box width="100%">
                    {isEditMode ? (
                        <>
                            <Box
                                style={{
                                    transform: 'translateY(-15px)',
                                    padding: '0 3px',
                                    position: 'relative',
                                    zIndex: 2
                                }}
                            >
                                <EditableTimelineWithSections
                                    onUpdateTime={updateTimeByPercentage}
                                    canvasElements={canvasRef?.current as Array<HTMLCanvasElement>}
                                    videoElement={videoRef?.current as HTMLVideoElement}
                                />
                            </Box>
                            <Box display="flex" justifyContent="space-between" mt={0.5} mb={1.5}>
                                <TrimTimePickers />
                                <SpacedGroup spacing={1}>
                                    <VideoEditorButtons
                                        canvasElements={
                                            canvasRef?.current as Array<HTMLCanvasElement>
                                        }
                                        playbookId={playbook.id}
                                        onUpdateTime={updateTimeBySeconds}
                                    />
                                </SpacedGroup>
                            </Box>
                        </>
                    ) : (
                        <Box
                            style={{
                                transform: 'translateY(-15px)',
                                position: 'relative',
                                zIndex: 2
                            }}
                        >
                            <TimelineWithSections
                                sections={playbook?.slicingSuggestion || []}
                                videoElement={videoRef?.current as HTMLVideoElement}
                            />
                        </Box>
                    )}
                </Box>
            </>
        )
    }
)
